import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { usePermissions } from '../context/PermissionContext';

interface Client {
  id: number;
  company_name?: string;
  contact_person?: string;
}

interface Tender {
  id: number;
  number: string;
  title: string;
  client_id?: number;
  client?: Client;
  organizer?: string;
  platform?: string;
  estimated_value: number;
  currency: string;
  status: string;
  submission_deadline?: string;
  result_date?: string;
  won_amount?: number;
  description?: string;
  notes?: string;
  file_url?: string;
  assigned_to?: string;
  created_at: string;
}

const statusConfig: Record<string, { label: string; color: string; icon: string }> = {
  preparing: { label: 'Tayyorlanmoqda', color: 'bg-blue-100 text-blue-700', icon: '📝' },
  submitted: { label: 'Topshirildi', color: 'bg-yellow-100 text-yellow-700', icon: '📤' },
  won:       { label: 'Yutdi',        color: 'bg-green-100 text-green-700', icon: '🏆' },
  lost:      { label: 'Yutqazdi',     color: 'bg-red-100 text-red-700', icon: '❌' },
  cancelled: { label: 'Bekor qilindi',color: 'bg-gray-100 text-gray-600', icon: '🚫' },
};

const currencySymbol: Record<string, string> = { usd: '$', eur: '€', uzs: "so'm" };

const emptyForm = {
  title: '', client_id: '', organizer: '', platform: '',
  estimated_value: 0, currency: 'uzs', status: 'preparing',
  submission_deadline: '', description: '', notes: '', assigned_to: '',
};

const Tenders: React.FC = () => {
  const { canEdit } = usePermissions();
  const canFull = canEdit('tenders');
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState<Tender | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      const t = await api.get('/tenders');
      setTenders(t.data);
    } catch (e) { console.error('Tenders:', e); }
    try {
      const c = await api.get('/clients');
      setClients(c.data);
    } catch (e) { setClients([]); }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = tenders.filter(t => {
    const matchSearch = t.number.toLowerCase().includes(search.toLowerCase()) ||
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      (t.organizer || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus ? t.status === filterStatus : true;
    return matchSearch && matchStatus;
  });

  const openAdd = () => {
    setForm(emptyForm); setEditId(null); setError(''); setShowModal(true);
  };

  const openEdit = (t: Tender) => {
    setForm({
      title: t.title, client_id: t.client_id?.toString() || '',
      organizer: t.organizer || '', platform: t.platform || '',
      estimated_value: t.estimated_value, currency: t.currency, status: t.status,
      submission_deadline: t.submission_deadline ? t.submission_deadline.split('T')[0] : '',
      description: t.description || '', notes: t.notes || '',
      assigned_to: t.assigned_to || '',
    });
    setEditId(t.id); setError(''); setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!canFull) return;
    if (!form.title.trim()) { setError("Sarlavha kiritilishi kerak"); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        ...form,
        client_id: form.client_id ? Number(form.client_id) : null,
        estimated_value: Number(form.estimated_value),
        submission_deadline: form.submission_deadline ? new Date(form.submission_deadline).toISOString() : null,
      };
      if (editId) await api.put(`/tenders/${editId}`, payload);
      else await api.post('/tenders', payload);
      setShowModal(false); fetchData();
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Xatolik yuz berdi');
    } finally { setLoading(false); }
  };

  const handleStatusChange = async (id: number, status: string) => {
    if (!canFull) return;
    const payload: any = { status };
    if (status === 'won') {
      const tender = tenders.find(t => t.id === id);
      const amount = prompt("Yutilgan summani kiriting:", tender?.estimated_value.toString() || '0');
      if (amount !== null) payload.won_amount = Number(amount);
    }
    await api.put(`/tenders/${id}`, payload);
    fetchData();
    if (showDetail) {
      const res = await api.get(`/tenders/${id}`);
      setShowDetail(res.data);
    }
  };

  const handleDelete = async (id: number) => {
    if (!canFull) return;
    if (!window.confirm("O'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/tenders/${id}`);
    setShowDetail(null); fetchData();
  };

  const handleFileUpload = async (tenderId: number, file: File) => {
    if (!canFull) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/tenders/${tenderId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchData();
      const res = await api.get(`/tenders/${tenderId}`);
      setShowDetail(res.data);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Fayl yuklashda xato');
    } finally { setUploadingFile(false); }
  };

  const handleFileDelete = async (tenderId: number) => {
    if (!canFull) return;
    if (!window.confirm("Faylni o'chirishni tasdiqlaysizmi?")) return;
    await api.delete(`/tenders/${tenderId}/upload`);
    fetchData();
    const res = await api.get(`/tenders/${tenderId}`);
    setShowDetail(res.data);
  };

  const getClientName = (client?: Client) =>
    client?.company_name || client?.contact_person || '—';

  const totalEstimated = tenders.filter(t => ['preparing', 'submitted'].includes(t.status))
    .reduce((sum, t) => sum + (t.currency === 'uzs' ? t.estimated_value : 0), 0);
  const wonValue = tenders.filter(t => t.status === 'won')
    .reduce((sum, t) => sum + (t.won_amount || t.estimated_value), 0);

  return (
    <div className="p-3 md:p-6">
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">🏛️ Tenderlar</h2>
        <button onClick={() => canFull && openAdd()} disabled={!canFull}
          title={!canFull ? "Faqat ko'rish huquqi" : undefined}
          className={`px-3 md:px-4 py-2 rounded-lg text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
          + <span className="hidden sm:inline">Yangi tender</span>
        </button>
      </div>

      {/* Statistika */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3 mb-4 md:mb-6">
        {Object.entries(statusConfig).map(([key, val]) => (
          <div key={key} className="bg-white rounded-xl shadow p-3 md:p-4">
            <p className="text-gray-500 text-xs">{val.icon} {val.label}</p>
            <p className="text-xl md:text-2xl font-bold text-gray-800">
              {tenders.filter(t => t.status === key).length}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 md:p-4">
          <p className="text-blue-600 text-xs md:text-sm">Jarayondagi tenderlar summasi (UZS)</p>
          <p className="text-xl md:text-2xl font-bold text-blue-700">{totalEstimated.toLocaleString()} so'm</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 md:p-4">
          <p className="text-green-600 text-xs md:text-sm">Yutilgan tenderlar qiymati</p>
          <p className="text-xl md:text-2xl font-bold text-green-700">{wonValue.toLocaleString()}</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 md:gap-3 mb-4">
        <input type="text" placeholder="🔍 Raqam, sarlavha yoki tashkilotchi..." value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Barcha holatlar</option>
          {Object.entries(statusConfig).map(([k, v]) =>
            <option key={k} value={k}>{v.label}</option>
          )}
        </select>
      </div>

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-3 md:px-4 py-3 text-left">Raqam</th>
              <th className="px-3 md:px-4 py-3 text-left">Sarlavha</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">Tashkilotchi</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">Mijoz</th>
              <th className="px-3 md:px-4 py-3 text-right hidden md:table-cell">Summa</th>
              <th className="px-3 md:px-4 py-3 text-left">Holat</th>
              <th className="px-3 md:px-4 py-3 text-left hidden md:table-cell">Muddat</th>
              <th className="px-3 md:px-4 py-3 text-center">Amallar</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-gray-400">Tenderlar yo'q</td></tr>
            ) : filtered.map(t => (
              <tr key={t.id} className="border-t hover:bg-gray-50">
                <td className="px-3 md:px-4 py-3 font-mono text-blue-600 font-medium text-xs md:text-sm">{t.number}</td>
                <td className="px-3 md:px-4 py-3 font-medium max-w-xs truncate">
                  {t.title}
                  {/* Mobil: yashiringan ustunlarning qisqacha ko'rinishi */}
                  <div className="md:hidden text-xs text-gray-400 mt-0.5 truncate font-normal">
                    {t.organizer && <>{t.organizer} · </>}{getClientName(t.client)} · {t.estimated_value.toLocaleString()} {currencySymbol[t.currency]}
                  </div>
                  {t.submission_deadline && (
                    <div className="md:hidden text-xs text-gray-400 mt-0.5 font-normal">
                      📅 {new Date(t.submission_deadline).toLocaleDateString('uz-UZ')}
                    </div>
                  )}
                </td>
                <td className="px-3 md:px-4 py-3 text-gray-600 hidden md:table-cell">{t.organizer || '—'}</td>
                <td className="px-3 md:px-4 py-3 text-gray-600 hidden md:table-cell">{getClientName(t.client)}</td>
                <td className="px-3 md:px-4 py-3 text-right font-bold hidden md:table-cell">
                  {t.estimated_value.toLocaleString()} {currencySymbol[t.currency]}
                </td>
                <td className="px-3 md:px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig[t.status]?.color}`}>
                    {statusConfig[t.status]?.icon} {statusConfig[t.status]?.label}
                  </span>
                </td>
                <td className="px-3 md:px-4 py-3 text-gray-500 text-xs hidden md:table-cell">
                  {t.submission_deadline ? new Date(t.submission_deadline).toLocaleDateString('uz-UZ') : '—'}
                </td>
                <td className="px-3 md:px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => setShowDetail(t)} title="Ko'rish"
                      className="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-xs">👁️</button>
                    <button onClick={() => canFull && openEdit(t)} disabled={!canFull}
                      title={!canFull ? "Faqat ko'rish huquqi" : "Tahrirlash"}
                      className={canFull ? "px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>✏️</button>
                    <button onClick={() => handleDelete(t.id)} disabled={!canFull}
                      title={!canFull ? "Faqat ko'rish huquqi" : "O'chirish"}
                      className={canFull ? "px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs" : "px-2 py-1 rounded text-xs bg-gray-100 text-gray-400 cursor-not-allowed"}>🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Yaratish/Tahrirlash Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-4 md:p-6 max-h-[95vh] overflow-y-auto">
            <h3 className="text-lg md:text-xl font-bold mb-4">
              {editId ? 'Tenderni tahrirlash' : 'Yangi tender'}
            </h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Sarlavha *</label>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="Tender mavzusi"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tashkilotchi</label>
                <input value={form.organizer} onChange={e => setForm({ ...form, organizer: e.target.value })}
                  placeholder="Davlat idorasi nomi"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Platforma</label>
                <input value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })}
                  placeholder="xarid.uzex.uz"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mijoz</label>
                <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="">— Tanlang —</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_person}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mas'ul hodim</label>
                <input value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}
                  placeholder="Ism Familiya"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Taxminiy summa</label>
                <input type="number" value={form.estimated_value} onChange={e => setForm({ ...form, estimated_value: Number(e.target.value) })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valyuta</label>
                <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="uzs">UZS — So'm</option>
                  <option value="usd">USD — Dollar</option>
                  <option value="eur">EUR — Euro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Topshirish muddati</label>
                <input type="date" value={form.submission_deadline} onChange={e => setForm({ ...form, submission_deadline: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Holat</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {Object.entries(statusConfig).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Tavsif</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3} placeholder="Tender tafsilotlari..."
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Izoh</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50">Bekor qilish</button>
              <button onClick={handleSubmit} disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-4 md:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <span className="font-mono text-blue-600 font-bold">{showDetail.number}</span>
                <h3 className="text-lg font-bold text-gray-800">{showDetail.title}</h3>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig[showDetail.status]?.color}`}>
                {statusConfig[showDetail.status]?.icon} {statusConfig[showDetail.status]?.label}
              </span>
            </div>

            {/* Holat o'zgartirish */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {Object.entries(statusConfig).map(([key, val]) => (
                <button key={key}
                  onClick={() => handleStatusChange(showDetail.id, key)}
                  disabled={showDetail.status === key || !canFull}
                  title={!canFull ? "Faqat ko'rish huquqi" : undefined}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border transition ${
                    showDetail.status === key || !canFull
                      ? 'opacity-50 cursor-not-allowed border-gray-200'
                      : 'hover:bg-gray-50 border-gray-300'
                  }`}>
                  {val.label}
                </button>
              ))}
            </div>

            <div className="space-y-2 text-sm mb-4">
              {showDetail.organizer && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Tashkilotchi</span>
                  <span className="font-medium">{showDetail.organizer}</span>
                </div>
              )}
              {showDetail.platform && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Platforma</span>
                  <span className="font-medium">{showDetail.platform}</span>
                </div>
              )}
              {showDetail.client && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Mijoz</span>
                  <span className="font-medium">{getClientName(showDetail.client)}</span>
                </div>
              )}
              <div className="flex justify-between border-b pb-2">
                <span className="text-gray-500">Taxminiy summa</span>
                <span className="font-bold text-blue-600">
                  {showDetail.estimated_value.toLocaleString()} {currencySymbol[showDetail.currency]}
                </span>
              </div>
              {showDetail.won_amount && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Yutilgan summa</span>
                  <span className="font-bold text-green-600">
                    {showDetail.won_amount.toLocaleString()} {currencySymbol[showDetail.currency]}
                  </span>
                </div>
              )}
              {showDetail.assigned_to && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Mas'ul hodim</span>
                  <span className="font-medium">{showDetail.assigned_to}</span>
                </div>
              )}
              {showDetail.submission_deadline && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Topshirish muddati</span>
                  <span>{new Date(showDetail.submission_deadline).toLocaleDateString('uz-UZ')}</span>
                </div>
              )}
              {showDetail.result_date && (
                <div className="flex justify-between border-b pb-2">
                  <span className="text-gray-500">Natija sanasi</span>
                  <span>{new Date(showDetail.result_date).toLocaleDateString('uz-UZ')}</span>
                </div>
              )}
            </div>

            {/* Fayl yuklash */}
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">📎 Tender hujjati</p>
              {showDetail.file_url ? (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 flex-wrap">
                  <span className="text-green-600 text-sm">✅ Fayl yuklangan</span>
                  <div className="flex gap-2 ml-auto">
                    <a href={`http://127.0.0.1:8000${showDetail.file_url}`}
                      target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs hover:bg-blue-200">
                      👁️ Ko'rish
                    </a>
                    {canFull && (
                      <button onClick={() => handleFileDelete(showDetail.id)}
                        className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-xs hover:bg-red-200">
                        🗑️ O'chirish
                      </button>
                    )}
                  </div>
                </div>
              ) : canFull ? (
                <label className={`flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition ${uploadingFile ? 'opacity-50' : ''}`}>
                  <span className="text-lg mb-1">📄</span>
                  <span className="text-xs text-gray-500">
                    {uploadingFile ? 'Yuklanmoqda...' : 'Tender hujjatini yuklang'}
                  </span>
                  <input type="file" className="hidden" disabled={uploadingFile}
                    onChange={e => e.target.files && handleFileUpload(showDetail.id, e.target.files[0])} />
                </label>
              ) : (
                <div className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-gray-200 rounded-lg text-gray-400">
                  <span className="text-xs">Fayl yuklanmagan</span>
                </div>
              )}
            </div>

            {showDetail.description && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Tavsif</p>
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">{showDetail.description}</div>
              </div>
            )}
            {showDetail.notes && (
              <div className="mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Izoh</p>
                <div className="bg-yellow-50 rounded-lg p-3 text-sm text-gray-700">{showDetail.notes}</div>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-4 flex-wrap">
              <button onClick={() => { if (canFull) { setShowDetail(null); openEdit(showDetail); } }}
                disabled={!canFull}
                title={!canFull ? "Faqat ko'rish huquqi" : "Tahrirlash"}
                className={`px-4 py-2 rounded-lg text-sm text-white ${canFull ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}>
                ✏️ Tahrirlash
              </button>
              <button onClick={() => setShowDetail(null)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Yopish</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tenders;
